import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "../../../utils/cn";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Popover = _ref => {
  let {
    id = "",
    children = undefined,
    childrenClassName = "",
    className = "",
    content = "",
    dark = false,
    position = "top",
    showArrow = false,
    trigger = "click",
    width = "",
    onClick = undefined
  } = _ref;
  const [show, setShow] = useState(false);
  const wrapperRef = useRef(null);
  const handleMouseOver = () => {
    if (trigger === "hover") {
      setShow(true);
    }
  };
  const handleMouseLeave = () => {
    if (trigger === "hover") {
      setShow(false);
    }
  };
  const handleOnClick = e => {
    setShow(!show);
    onClick && onClick();
  };
  useEffect(() => {
    // get bounding coordinates of element
    const childElement = document.getElementById("popover-".concat(id, "-trigger-element"));
    const {
      offsetHeight: childHeight,
      offsetWidth: childWidth
    } = childElement;
    // get popover and arrow elements
    const popover = document.getElementById("popover-".concat(id, "-container"));
    const {
      offsetHeight: popoverHeight
    } = popover;
    const arrow = document.getElementById("popover-arrow-".concat(id));
    let arrowHeight, arrowWidth;
    if (showArrow) {
      arrowHeight = arrow.offsetHeight;
      arrowWidth = arrow.offsetWidth;
    }
    const offset = showArrow ? 10 : 0;
    const containerMargin = 10;
    const arrowMargin = 4;

    // set position of popover and arrow relative to child element
    switch (position) {
      case "top":
        popover.style.bottom = "".concat(childHeight + offset + containerMargin, "px");
        if (showArrow) {
          arrow.style.bottom = "".concat(childHeight + offset + arrowMargin, "px");
        }
        break;
      case "right":
        popover.style.left = "".concat(childWidth + offset + containerMargin, "px");
        popover.style.bottom = "".concat(childHeight / 2 - popoverHeight / 2, "px");
        if (showArrow) {
          arrow.style.left = "".concat(childWidth + offset + arrowMargin, "px");
          arrow.style.bottom = "".concat(childHeight / 2 - arrowHeight / 2, "px");
        }
        break;
      case "bottom":
        popover.style.top = "".concat(childHeight + offset + containerMargin, "px");
        if (showArrow) {
          arrow.style.top = "".concat(childHeight + offset + arrowMargin, "px");
        }
        break;
      case "left":
        popover.style.right = "".concat(childWidth + offset + containerMargin, "px");
        popover.style.bottom = "".concat(childHeight / 2 - popoverHeight / 2, "px");
        if (showArrow) {
          arrow.style.right = "".concat(childWidth + offset + arrowMargin, "px");
          arrow.style.bottom = "".concat(childHeight / 2 - arrowHeight / 2, "px");
        }
        break;
      default:
        return;
    }
  }, []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShow(false);
      }
    }
    if (show) {
      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [show, wrapperRef]);
  const containerClass = () => cn("absolute z-10 text-xs", {
    "text-white": dark,
    invisible: !show
  });
  const contentClass = () => cn("border border-solid p-4", {
    "border-light-border-blue rounded bg-white": !dark,
    "rounded bg-text-blue": dark
  }, className, "".concat(width ? "w-".concat(width) : ""));
  const arrowClass = () => cn("h-3 w-3 border-b border-solid border-r border-light-border-blue transform absolute z-20", {
    "bg-white": !dark,
    "bg-text-blue": dark,
    "rotate-45": position === "top",
    "-rotate-135": position === "bottom",
    "rotate-135": position === "right",
    "-rotate-45": position === "left",
    invisible: !show
  });
  return /*#__PURE__*/_jsxs("div", {
    id: id,
    "data-testid": id,
    ref: wrapperRef,
    onMouseEnter: handleMouseOver,
    onMouseLeave: handleMouseLeave,
    className: "relative flex justify-center",
    children: [showArrow && /*#__PURE__*/_jsx("div", {
      id: "popover-arrow-".concat(id),
      className: arrowClass(),
      "data-testid": "popover-arrow-".concat(id)
    }), /*#__PURE__*/_jsx("div", {
      className: containerClass(),
      id: "popover-".concat(id, "-container"),
      "data-testid": "popover-".concat(id, "-container"),
      children: /*#__PURE__*/_jsx("div", {
        className: contentClass(),
        "data-testid": "popover-content",
        children: /*#__PURE__*/_jsx("span", {
          id: "popover-".concat(id, "-content"),
          children: content
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      id: "popover-".concat(id, "-trigger-element"),
      className: childrenClassName,
      "data-testid": "popover-".concat(id, "-trigger-element"),
      onClick: handleOnClick,
      children: children
    })]
  });
};
Popover.propTypes = {
  /** className for reference */
  className: PropTypes.string,
  /** Content data */
  content: PropTypes.node,
  /** Dark mode */
  dark: PropTypes.bool,
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** position of popover */
  position: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  /** include arrow on popover */
  showArrow: PropTypes.bool,
  /** action to trigger popover */
  trigger: PropTypes.string,
  /** Width of popover content. Can also be set using className prop. */
  width: PropTypes.string,
  /** onClick handler */
  onClick: PropTypes.func
};
export default Popover;
//# sourceMappingURL=Popover.js.map